import React from 'react';
import {
  Checkbox,
  FlexContainer,
  FormLabel,
  Modal,
  PrimaryButton,
  Radio,
  SecondaryButton,
  Select,
  Spacer,
  Spinner,
  TextInput,
} from '../../common';
import { useTranslationsController } from '../../../utils/globalTranslationsController';
import { verifyInformationTranslationObject } from './translations';
import { Notice } from '../../common/Notice';
import { Link, RadioGroup } from '@material-ui/core';
import {
  getCountriesSortedByName,
  taxInfoFields,
} from '../../pages/Register/data';
import s from './VerifyInformation.styles';
import { formFields } from '../../pages/Profile/data';
import { useVerifyInformation } from './useVerifyInformation';
import { handleLogout } from '../../../utils/logout.utils';
import { isPostalRequired } from '../../../utils/localisation_countries.utils';
import { useNavigation } from '../Navigation/useNavigation';
import { onClickOpenUrl } from '../../../utils/link.utils';
import { ErrorMessageText } from '../SocialContainer/LoginButtons/FacebookLogin/FacebookLogin.styles';
import { validFormat } from '../../../utils/vatValidation.utils';

interface Props {
  isOpen: boolean;
  isMobile: boolean;
  acceptTerms: () => void;
}

export const VerifyInformation: React.FC<Props> = ({ isOpen, acceptTerms }) => {
  const {
    taxInformation,
    taxDispatch,
    isMobile,
    onSubmit,
    details,
    handleDetailsChange,
    selfBillAccepted,
    toggleSelfbill,
    disableSubmit,
    validVAT,
    error,
    isLoading,
  } = useVerifyInformation(acceptTerms);

  const { content: t } = useTranslationsController(
    verifyInformationTranslationObject
  );

  const { language } = useNavigation();
  const link = `/about/terms${language ? '.' + language : ''}.html#selfbilling`;

  return (
    <Modal
      isOpen={isOpen}
      fullScreen={isMobile}
      titleText={t['SELFBILL.MODAL.HEADER']}
      actions={
        <>
          <SecondaryButton onClick={handleLogout}>
            {t['APP.NAV.LOGOUT']}
          </SecondaryButton>
          <PrimaryButton
            onClick={onSubmit}
            disabled={!selfBillAccepted || disableSubmit || isLoading}
            aria-disabled={!selfBillAccepted || disableSubmit || isLoading}
          >
            {isLoading ? <Spinner /> : <>{t['GENERIC.SUBMIT']}</>}
          </PrimaryButton>
        </>
      }
    >
      <s.Wrapper>
        <Notice
          variant={'info'}
          message={t['SELFBILL.MODAL.NOTICE']}
          testId={'t_selfbill_modal_notice'}
        />
        <Spacer amount={16} isVertical />
        <form onSubmit={e => e.preventDefault()}>
          <Select
            testId="t_input_country"
            title={t['REGISTER.DETAILS.LABEL.COUNTRY']}
            value={details.country}
            onChange={e =>
              handleDetailsChange(formFields.COUNTRY, e.currentTarget.value)
            }
            options={getCountriesSortedByName()}
            isFullWidth
          />
          <Spacer amount={16} isVertical />
          <FormLabel id="label_address1" isBold>
            {t['REGISTER.DETAILS.LABEL.ADDRESS1']}
          </FormLabel>
          <TextInput
            data-testid="t_input_address1"
            aria-labelledby="label_address1 form_title"
            value={details.address1}
            onChange={e =>
              handleDetailsChange(formFields.ADDRESS1, e.currentTarget.value)
            }
          />
          <Spacer amount={16} isVertical />
          <FormLabel id="label_address2" isBold>
            {t['REGISTER.DETAILS.LABEL.ADDRESS2']}
          </FormLabel>
          <TextInput
            data-testid="t_input_address2"
            aria-labelledby="label_address2 form_title"
            value={details.address2}
            onChange={e =>
              handleDetailsChange(formFields.ADDRESS2, e.currentTarget.value)
            }
          />
          <Spacer amount={16} isVertical />
          <FormLabel id="label_city" isBold>
            {t['REGISTER.DETAILS.LABEL.CITY']}
          </FormLabel>
          <TextInput
            data-testid="t_input_address"
            aria-labelledby="label_address form_title"
            value={details.city}
            onChange={e =>
              handleDetailsChange(formFields.CITY, e.currentTarget.value)
            }
          />
          <Spacer amount={16} isVertical />
          {isPostalRequired(details.country) && (
            <>
              <FlexContainer
                flexDirection="column"
                fullWidth
                alignItems="flex-start"
              >
                <FormLabel id="label_postCode" isBold>
                  {t['REGISTER.DETAILS.LABEL.POSTCODE']}
                </FormLabel>
                <TextInput
                  data-testid="t_input_postcode"
                  aria-labelledby="label_postCode form_title"
                  value={details.postCode}
                  onChange={e =>
                    handleDetailsChange(
                      formFields.POST_CODE,
                      e.currentTarget.value
                    )
                  }
                />
              </FlexContainer>
            </>
          )}
          <Spacer amount={16} isVertical />
          <FormLabel hasBottomPadding={false} isBold={false}>
            {t['PROFILE.TAX_INFORMATION.VAT']}
            <RadioGroup
              aria-labelledby="vat_registered"
              value={taxInformation.isVATRegistered}
              data-testid={`t_vat_reg`}
              name="vat"
              row
              onChange={e =>
                taxDispatch({
                  type: taxInfoFields.IS_VAT_REGISTERED,
                  payload: e.currentTarget.value,
                })
              }
            >
              <Radio
                label={'Yes'}
                testId={'t_vat_registered_yes'}
                value="yes"
              />
              <Radio label={'No'} testId={'t_vat_registered_no'} value="no" />
            </RadioGroup>
          </FormLabel>
          <Spacer amount={16} isVertical />
          {taxInformation.isVATRegistered === 'yes' && (
            <>
              <FlexContainer
                flexDirection="column"
                fullWidth
                alignItems="flex-start"
              >
                <FormLabel id="label_vat" isBold>
                  {t['PROFILE.TAX_INFORMATION.VAT_NUMBER']}
                </FormLabel>
                <TextInput
                  data-testid="t_input_vat"
                  aria-labelledby="label_vat form_title"
                  placeholder={''}
                  value={taxInformation.vatNumber}
                  onChange={e =>
                    taxDispatch({
                      type: taxInfoFields.VAT_NUMBER,
                      payload: e.currentTarget.value,
                    })
                  }
                />
              </FlexContainer>
              <Spacer amount={8} isVertical />
              {!validVAT && (
                <ErrorMessageText>{`The VAT number seems incorrect. The VAT for ${details.country} should look like this ${validFormat[details.country]}`}</ErrorMessageText>
              )}
              <Spacer amount={16} isVertical />
            </>
          )}
          <FormLabel hasBottomPadding={false} isBold={false}>
            {t['PROFILE.TAX_INFORMATION.LLC']}
            <RadioGroup
              aria-labelledby="is_llc"
              value={taxInformation.isLLC}
              data-testid={`t_is_llc`}
              name="llc"
              row
              onChange={e =>
                taxDispatch({
                  type: taxInfoFields.IS_LLC,
                  payload: e.currentTarget.value,
                })
              }
            >
              <Radio label={'Yes'} testId={'t_llc_yes'} value="yes" />
              <Radio label={'No'} testId={'t_llc_no'} value="no" />
            </RadioGroup>
          </FormLabel>
          <Spacer amount={16} isVertical />
          {taxInformation.isLLC === 'yes' && (
            <>
              <s.DualFieldWrapper justifyContent="space-between" fullWidth>
                <FlexContainer
                  flexDirection="column"
                  fullWidth
                  alignItems="flex-start"
                >
                  <FormLabel id="label_cname" isBold>
                    {t['PROFILE.TAX_INFORMATION.LLC_NAME']}
                  </FormLabel>
                  <TextInput
                    data-testid="t_input_cname"
                    aria-labelledby="label_cname form_title"
                    placeholder={''}
                    value={taxInformation.companyName}
                    onChange={e =>
                      taxDispatch({
                        type: taxInfoFields.COMPANY_NAME,
                        payload: e.currentTarget.value,
                      })
                    }
                  />
                </FlexContainer>
                <Spacer amount={16} />
                <FlexContainer
                  flexDirection="column"
                  fullWidth
                  alignItems="flex-start"
                >
                  <FormLabel id="label_cnumber" isBold>
                    {t['PROFILE.TAX_INFORMATION.LLC_NUMBER']}
                  </FormLabel>
                  <TextInput
                    data-testid="t_input_cnumber"
                    aria-labelledby="label_cnumber form_title"
                    placeholder={''}
                    value={taxInformation.companyNumber}
                    onChange={e =>
                      taxDispatch({
                        type: taxInfoFields.COMPANY_NUMBER,
                        payload: e.currentTarget.value,
                      })
                    }
                  />
                </FlexContainer>
              </s.DualFieldWrapper>
              <Spacer amount={16} isVertical />
            </>
          )}
          {['DK', 'NO'].includes(details.country) && (
            <FormLabel hasBottomPadding={false} isBold={false}>
              {t['PROFILE.TAX_INFORMATION.RECEIVE_VAT']}
              <RadioGroup
                aria-labelledby="receive_vat"
                value={taxInformation.receiveVAT}
                data-testid={`t_receive_vat`}
                name="receive_vat"
                row
                onChange={e =>
                  taxDispatch({
                    type: taxInfoFields.RECEIVE_VAT,
                    payload: e.currentTarget.value,
                  })
                }
              >
                <Radio label={'Yes'} testId={'t_receive_vat_yes'} value="yes" />
                <Radio label={'No'} testId={'t_receive_vat_no'} value="no" />
              </RadioGroup>
            </FormLabel>
          )}

          <Checkbox
            label={
              <>
                {t['SELFBILL.MODAL.AGREEMENT']}
                <Link onClick={onClickOpenUrl(link)}>
                  {t['SELFBILL.MODAL.TERMS']}
                </Link>
              </>
            }
            testId={'t_agreement'}
            value={selfBillAccepted}
            onClick={toggleSelfbill}
          />
        </form>
      </s.Wrapper>
      {error && (
        <Notice variant={'alert'} message={error} testId={'t_error_notice'} />
      )}
    </Modal>
  );
};
